import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraChatMessage, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Copy } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { batchMessageParts } from '../../utils/batchMessageParts';
import { copyToClipboard } from '../../utils/downloadUtils';
import { toast } from '../toast';
import { MessageAvatar } from './MessageAvatar';
import { MessagePartRenderer } from './message-parts';
import { ProgressPart } from './message-parts/ProgressPart';
import { isWorkroomOutcomeTool } from './message-parts/ToolPart';
import { deliverableImportFeedbackText } from './shanks-tools/deliverable-import/card';
import { ToolProgress } from './ToolProgressPanel';
import { isMessagePartText, isMessagePartTool } from './types';
// Product rule (SAD-389): prose emitted before a tool call is a status line
// ("Checking the latest result now…"), never the answer — the model wrote it
// from memory before the tool result came back. Post-tool text is always the
// answer. A text part collapses to the subdued status affordance only when a
// tool part follows it AND another text part follows that tool part — i.e.
// there's a later answer superseding it. This also gives the right live
// behaviour for free: while pre-tool prose is still streaming there's no
// later text part yet (renders as prose); the instant the post-tool answer
// starts streaming, it collapses. No streaming-specific state needed.
function collapsibleStatusTextParts(parts) {
    const collapse = new Set();
    for(let i = 0; i < parts.length; i++){
        const part = parts[i];
        if (!isMessagePartText(part)) continue;
        const toolIndex = parts.findIndex((p, j)=>j > i && isMessagePartTool(p));
        if (toolIndex === -1) continue;
        const hasLaterText = parts.some((p, k)=>k > toolIndex && isMessagePartText(p));
        if (hasLaterText) collapse.add(part);
    }
    return collapse;
}
const STATUS_LEAD_MAX_CHARS = 80;
/**
 * The first sentence (its terminal punctuation dropped in favor of the
 * ellipsis) or ~80 characters, whichever is shorter — never the full text.
 * Truncated in the string, not with CSS: no title attribute or hidden
 * element carries the rest, so a collapsed status line never puts the full
 * pre-tool text anywhere in the DOM. The full text remains in the persisted
 * message record server-side.
 */ function statusLeadText(text) {
    const trimmed = text.trim();
    const sentenceEnd = trimmed.search(/[.!?](?:\s|$)/);
    const cut = sentenceEnd === -1 ? Math.min(trimmed.length, STATUS_LEAD_MAX_CHARS) : Math.min(sentenceEnd, STATUS_LEAD_MAX_CHARS);
    return `${trimmed.slice(0, cut).trimEnd()}…`;
}
/** Subdued status-line rendering of a pre-tool text part once a later text
 * part has superseded it as the answer. */ function StatusTextPart({ text, messageId, index }) {
    return /*#__PURE__*/ _jsx("p", {
        className: "text-sm italic text-muted-foreground",
        "data-testid": Testids.assistant.messageStatusText,
        children: statusLeadText(text)
    }, `${messageId}-status-${index}`);
}
/**
 * Copies the assistant's prose answer. Text-only by design: reasoning is
 * hidden chain-of-thought, and tool/artifact parts carry their own copy
 * affordances — so this aggregates just the `text` parts. Inline Copy→Check
 * swap (2s) matches the app's other copy buttons (e.g. CopyJsonButton).
 */ function MessageCopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(async ()=>{
        const ok = await copyToClipboard(text);
        if (ok) {
            setCopied(true);
            setTimeout(()=>setCopied(false), 2000);
        } else {
            toast({
                type: 'error',
                title: 'Could not copy to clipboard'
            });
        }
    }, [
        text
    ]);
    return /*#__PURE__*/ _jsx(SpecraIconButton, {
        label: copied ? 'Copied' : 'Copy',
        onClick: handleCopy,
        "data-testid": Testids.assistant.messageCopy,
        size: "sm",
        variant: "ghost",
        className: "text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
        children: copied ? /*#__PURE__*/ _jsx(Check, {
            "aria-hidden": "true"
        }) : /*#__PURE__*/ _jsx(Copy, {
            "aria-hidden": "true"
        })
    });
}
/**
 * Renders a single chat message with avatar and content
 * @param props Component props
 * @returns Chat message component
 */ /**
 * The message body — the reused part-renderer. Shared by every frame variant,
 * so the workroom (Specra) frame renders exactly the same content (text, tools,
 * artifacts, citations…) the default /assistant frame does. No feature is lost.
 */ function MessageParts({ message, chatId, variant, isActive }) {
    const items = batchMessageParts(message.parts);
    const tools = message.role === 'assistant' ? message.parts.filter(isMessagePartTool) : [];
    const firstTool = tools[0];
    // Which text parts are stale pre-tool prose superseded by a later answer.
    const statusTextParts = useMemo(()=>collapsibleStatusTextParts(message.parts), [
        message.parts
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: items.map((batchedItem, index)=>{
            if (batchedItem.type === 'progress-batch') {
                return /*#__PURE__*/ _jsx(ProgressPart, {
                    progressParts: batchedItem.parts,
                    messageId: message.id ?? ''
                }, `${message.id}-progress-batch-${index}`);
            }
            if (isMessagePartTool(batchedItem.part) && message.role === 'assistant') {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        batchedItem.part === firstTool && /*#__PURE__*/ _jsx(ToolProgress, {
                            parts: tools,
                            active: isActive
                        }),
                        isWorkroomOutcomeTool(batchedItem.part) && /*#__PURE__*/ _jsx(MessagePartRenderer, {
                            part: batchedItem.part,
                            messageId: message.id ?? '',
                            index: index,
                            chatId: chatId,
                            variant: variant
                        })
                    ]
                }, `${message.id}-tool-${batchedItem.part.toolCallId || index}`);
            }
            if (isMessagePartText(batchedItem.part) && statusTextParts.has(batchedItem.part)) {
                return /*#__PURE__*/ _jsx(StatusTextPart, {
                    text: batchedItem.part.text,
                    messageId: message.id ?? '',
                    index: index
                }, `${message.id}-status-${index}`);
            }
            return /*#__PURE__*/ _jsx(MessagePartRenderer, {
                part: message.role === 'user' && isMessagePartText(batchedItem.part) ? {
                    ...batchedItem.part,
                    text: deliverableImportFeedbackText(batchedItem.part.text)
                } : batchedItem.part,
                messageId: message.id ?? '',
                index: index,
                chatId: chatId,
                variant: variant
            }, `${message.id}-part-${index}`);
        })
    });
}
export function ChatMessageView({ message, chatId, variant = 'default', isActive = false }) {
    const isAssistant = message.role === 'assistant';
    // Assistant prose, aggregated across (possibly tool-interleaved) text parts.
    const copyText = isAssistant ? message.parts.filter(isMessagePartText).map((p)=>p.text).join('\n\n').trim() : '';
    const copyButton = copyText ? /*#__PURE__*/ _jsx(MessageCopyButton, {
        text: copyText
    }) : undefined;
    // S7 — the workroom renders each message through the Specra chat-message
    // frame (logo mark for the assistant, avatar for the user; the author label
    // and copy action fill the frame's slots). The body is the same reused
    // part-renderer, so no chat feature is lost.
    if (variant === 'specra') {
        return /*#__PURE__*/ _jsx(SpecraChatMessage, {
            author: isAssistant ? 'Assistant' : 'You',
            speaker: isAssistant ? 'assistant' : 'user',
            showAuthor: false,
            actions: copyButton,
            className: "group",
            "data-testid": Testids.specraChat.message,
            children: /*#__PURE__*/ _jsx("div", {
                className: "min-w-0 space-y-2 overflow-x-auto break-words",
                children: /*#__PURE__*/ _jsx(MessageParts, {
                    message: message,
                    chatId: chatId,
                    variant: "specra",
                    isActive: isActive
                })
            })
        });
    }
    const isAssist = variant === 'assist';
    return /*#__PURE__*/ _jsxs("div", {
        className: `group flex items-start ${isAssist ? 'gap-[9px]' : 'gap-4'}`,
        children: [
            /*#__PURE__*/ _jsx(MessageAvatar, {
                isAssistant: isAssistant,
                variant: variant
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `min-w-0 flex-1 break-words overflow-x-auto text-foreground ${isAssist ? 'space-y-2 pt-0.5 text-[12.5px] leading-[1.6]' : 'space-y-2 pt-1 max-w-full md:max-w-[100%]'}`,
                children: [
                    /*#__PURE__*/ _jsx(MessageParts, {
                        message: message,
                        chatId: chatId,
                        variant: variant,
                        isActive: isActive
                    }),
                    copyText && /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-start pt-1",
                        children: /*#__PURE__*/ _jsx(MessageCopyButton, {
                            text: copyText
                        })
                    })
                ]
            })
        ]
    });
}
